<template>
    <div>
        <Head :title="`${form.first_name} ${form.last_name}`"/>
        <div class="flex justify-start mb-8 max-w-3xl">
            <h1 class="text-3xl font-bold">
                <Link class="text-indigo-400 hover:text-indigo-600" :href="route('users.index')">
                    Користувачі
                </Link>
                <span class="text-indigo-400 font-medium">/</span>
                {{ form.first_name }} {{ form.last_name }}
            </h1>
        </div>
        <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <text-input v-model="form.first_name" :error="form.errors.first_name"
                                class="pb-8 pr-6 w-full lg:w-1/2" label="Ім'я"/>
                    <text-input v-model="form.last_name" :error="form.errors.last_name"
                                class="pb-8 pr-6 w-full lg:w-1/2" label="Прізвище"/>
                    <text-input v-model="form.phone_number" :error="form.errors.phone_number"
                                class="pb-8 pr-6 w-full lg:w-1/2"
                                label="Телефон"/>
                    <select-input v-model="form.owner" :error="form.errors.owner" class="pb-8 pr-6 w-full lg:w-1/2"
                                  label="Адмін">
                        <option :value="true">Так</option>
                        <option :value="false">Ні</option>
                    </select-input>
                    <select-input v-model="form.watchman" :error="form.errors.watchman"
                                  class="pb-8 pr-6 w-full lg:w-1/2"
                                  label="Сторож">
                        <option :value="true">Так</option>
                        <option :value="false">Ні</option>
                    </select-input>
                    <checkbox
                        v-model="form.blocked"
                        label="Заблоковано"
                    />
                    <select-input v-model="form.annual_subscription" :error="form.errors.annual_subscription"
                                  class="pb-4 pt-4 pr-6 w-full lg:w-1/2"
                                  label="Річна підписка">
                        <option :value="true">Так</option>
                        <option :value="false">Ні</option>
                    </select-input>
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <button v-if="!user.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button"
                            @click="destroy">
                        Видалити користувача
                    </button>
                    <Link v-if="!user.deleted_at" :href="route('users.impersonate', user.id)" method="post" as="button"
                          class="ml-6 text-indigo-600 hover:underline text-sm" tabindex="-1" type="button">
                        Увійти як користувач
                    </Link>
                    <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
                        Оновити користувача
                    </loading-button>
                </div>
            </form>
        </div>

        <!-- Balance adjustment -->
        <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden mt-6">
            <div class="p-8">
                <h2 class="text-lg font-semibold mb-1">Внутрішній баланс</h2>
                <p class="text-2xl font-bold text-indigo-700 mb-4">
                    {{ Number(user.internal_balance).toFixed(2) }} грн
                </p>
                <div class="flex gap-6 mb-6 text-sm">
                    <div>
                        <span class="text-gray-500">Загальна сума замовлень:</span>
                        <span class="ml-1 font-semibold text-gray-800">{{ user.total_orders_amount }} грн</span>
                    </div>
                    <div>
                        <span class="text-gray-500">Поточний рівень бонусу:</span>
                        <span class="ml-1 font-semibold" :class="user.bonus_percent > 0 ? 'text-indigo-700' : 'text-gray-500'">
                            {{ user.bonus_percent }}%
                        </span>
                    </div>
                </div>
                <form @submit.prevent="adjustBalance">
                    <div class="flex flex-wrap gap-4 items-end">
                        <div class="flex flex-col">
                            <label class="text-sm font-medium text-gray-700 mb-1">Сума (грн)</label>
                            <input
                                v-model="balanceForm.amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                placeholder="0.00"
                                required
                                class="border border-gray-300 rounded-lg px-3 py-2 w-40 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        <div class="flex flex-col flex-1 min-w-48">
                            <label class="text-sm font-medium text-gray-700 mb-1">Коментар (необов'язково)</label>
                            <input
                                v-model="balanceForm.note"
                                type="text"
                                placeholder="Причина коригування..."
                                class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        <button type="submit" :disabled="balanceForm.processing"
                                @click="balanceForm.operation = 'add'"
                                class="h-10 px-5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition">
                            Поповнити
                        </button>
                        <button type="submit" :disabled="balanceForm.processing"
                                @click="balanceForm.operation = 'remove'"
                                class="h-10 px-5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition">
                            Зняти
                        </button>
                    </div>
                    <p v-if="balanceForm.errors.amount" class="mt-2 text-sm text-red-600">{{ balanceForm.errors.amount }}</p>
                </form>
            </div>
        </div>

        <!-- Balance history -->
        <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden mt-6">
            <div class="p-8">
                <h2 class="text-lg font-semibold mb-4">Історія балансу</h2>
                <div v-if="balanceHistory.length === 0" class="text-sm text-gray-400">
                    Коригувань ще не було.
                </div>
                <table v-else class="w-full text-sm">
                    <thead>
                        <tr class="text-left text-gray-500 border-b">
                            <th class="pb-2 pr-4">Дата</th>
                            <th class="pb-2 pr-4">Тип</th>
                            <th class="pb-2 pr-4">Сума</th>
                            <th class="pb-2 pr-4">Коментар</th>
                            <th class="pb-2">Адмін</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in balanceHistory" :key="row.id" class="border-b last:border-0">
                            <td class="py-2 pr-4 text-gray-500 whitespace-nowrap">{{ row.created_at }}</td>
                            <td class="py-2 pr-4">
                                <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="typeBadgeClass(row.type)">
                                    {{ typeLabel(row.type) }}
                                </span>
                            </td>
                            <td class="py-2 pr-4 font-medium" :class="isDebit(row) ? 'text-red-600' : 'text-green-700'">
                                {{ isDebit(row) ? '-' : '+' }}{{ Math.abs(row.amount).toFixed(2) }} грн
                            </td>
                            <td class="py-2 pr-4 text-gray-600">
                                <template v-if="row.order_id">замовлення #{{ row.order_id }}</template>
                                <br/>
                                <template v-if="row.note">{{ row.note || '—' }}</template>
                            </td>
                            <td class="py-2 text-gray-500">{{ row.admin_name }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import {Head, Link} from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import {route} from "ziggy-js";
import Checkbox from "@/Shared/Checkbox.vue";

export default {
    components: {
        Checkbox,
        FileInput,
        Head,
        Link,
        LoadingButton,
        SelectInput,
        TextInput,
        TrashedMessage,
    },
    layout: Layout,
    props: {
        user:           Object,
        balanceHistory: {type: Array, default: () => []},
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                _method: 'put',
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                phone_number: this.user.phone_number,
                owner: this.user.owner,
                blocked: this.user.blocked,
                watchman: this.user.watchman,
                annual_subscription: this.user.annual_subscription,
            }),
            balanceForm: this.$inertia.form({
                amount:    null,
                operation: 'add',
                note:      '',
            }),
        }
    },
    methods: {
        route,
        update() {
            this.form.put(this.$route('users.update', this.user.id))
        },
        destroy() {
            if (confirm('Дійсно видалити?')) {
                this.$inertia.delete(this.$route('users.destroy', this.user.id))
            }
        },
        isDebit(row) {
            return row.amount < 0 || ['manual_remove', 'order_payment'].includes(row.type)
        },
        typeLabel(type) {
            return {
                manual_add:    'Поповнення',
                manual_remove: 'Зняття',
                liqpay:        'LiqPay',
                order_bonus:   'Бонус',
                order_payment: 'Оплата замовлення',
                order_refund:  'Повернення',
            }[type] ?? type
        },
        typeBadgeClass(type) {
            if (type === 'manual_remove') return 'bg-red-100 text-red-600'
            if (type === 'order_payment') return 'bg-red-100 text-red-600'
            if (type === 'liqpay')        return 'bg-blue-100 text-blue-700'
            if (type === 'order_bonus')   return 'bg-purple-100 text-purple-700'
            if (type === 'order_refund')  return 'bg-green-100 text-green-700'
            return 'bg-green-100 text-green-700'
        },
        adjustBalance() {
            this.balanceForm.post(this.$route('users.balance.adjust', this.user.id), {
                onSuccess: () => { this.balanceForm.reset('amount', 'note') },
            })
        },
    },
}
</script>
